import React from "react";

import { isPlainObject } from "lodash-es";
import { validate as validateUUID } from "uuid";

import { GenericObject } from "src/api/flowTypes";
import { DisplayDateTime } from "src/base-components/Date";
import { MultilineDataValue } from "src/base-components/MultilineDataValue";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import {
  LinkValue,
  TruncatedText,
} from "src/cases/caseView/blocks/TypedCellDisplay";
import { TableBlockSingleRecord } from "src/cases/queries/casesTypes";
import { JSONValueViewer } from "src/dataTable/DetailedView/JSONValueViewer";
import { isISO8601 } from "src/utils/datetime";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { isProbablyMarkdown, isValidURL } from "src/utils/stringUtils";

const ClampedText: React.FC<{ value: string }> = ({ value }) => (
  <TruncatedText value={value}>
    <div className="line-clamp-3 min-w-0 break-words text-gray-800">
      {value}
    </div>
  </TruncatedText>
);

const renderValue = (value: unknown, key: string): React.ReactNode => {
  if (typeof value === "number") {
    return <span className="font-code-12">{value}</span>;
  }

  if (typeof value === "boolean" || value === null) {
    return speakPythonPrimitive(String(value));
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    return <JSONValueViewer field={key} value={value as GenericObject} />;
  }

  if (typeof value === "string" && isISO8601(value)) {
    return (
      <TruncatedText value={value}>
        <div className="truncate">
          <DisplayDateTime value={value} />
        </div>
      </TruncatedText>
    );
  }

  if (typeof value === "string" && validateUUID(value)) {
    return <TruncatedIdPill feedback="inline-only-checkmark" id={value} />;
  }

  if (typeof value === "string" && isValidURL(value)) {
    return <LinkValue value={value} />;
  }

  if (typeof value === "string" && isProbablyMarkdown(value)) {
    return <MultilineDataValue fieldName={key} value={value} />;
  }

  if (typeof value === "string") {
    return <ClampedText value={value} />;
  }

  return String(value);
};

export const SingleRecordDisplay: React.FC<{
  record: TableBlockSingleRecord;
}> = ({ record }) => {
  const items: PropertiesListItem[] = Object.entries(record).map(
    ([key, value]) => ({
      key,
      label: key,
      value: renderValue(value, key),
    }),
  );

  return (
    <PropertiesList
      dataLocPrefix="table-block"
      items={items}
      rightAlignValues={true}
      showIcons={false}
    />
  );
};
